'use client';
import React, { useMemo, useState, useRef } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { cn } from '@/utils/classnames';
import { useTranslations } from 'next-intl';
import '../form/edit.css';
import { VerificationCodeInput } from '../form/login-input';
import { Button } from '@/components/ui/button';
import { startCountdown } from '../../util';
import { useEmailBind, useEmailBindSendCode, usePhoneBind, useBindSendCode, useBlackCheck } from '../../services/user-auth';
import toast from 'react-hot-toast';
import { defaultPhoneCountry } from '../form/country-codes';
import '@/features/user-center/styles/btn.css';
import ModalTitle from '../modal-title';
import { isEmail } from '@/utils/validation';
import { useIpCountry } from '@/features/auth/hooks/use-ip-country';
import { useCountries } from '@/features/auth/hooks/use-countries';
import Image from 'next/image';
import { clsx } from 'clsx';
import iconClose from '@/features/auth/images/icon/btn_x.png';
import { useAuthError } from '../../hooks/useAuthError';
import { awaitTo } from '@/utils/tools';
import { VerifyMethod } from '../../constants';
import { useDeviceID } from '@/components/providers/device-id-provider';
import { BlackStatus } from '@/features/auth/services/black-check';
import useCfTokenCodeStatus from '../../hooks/useCfTokenCodeStatus';
import useUserInfo from '../../hooks/useUserInfo';
import { track } from '@/utils/track';


interface ModalEmailOrPhoneEditProps {
  /** Custom styles */
  className?: string;
  /** Callback function when modal closes */
  onClose?: () => void;
  /** Callback function when edit succeeds */
  onSuccess?: () => void;
  /** Cancel button text */
  cancelText?: string;
}

// 输入类型枚举
enum InputType {
  EMAIL = 'email',
  PHONE = 'phone'
}

const ModalEmailOrPhoneEdit = NiceModal.create<ModalEmailOrPhoneEditProps>(({
  className,
  onClose,
  onSuccess,
  cancelText
}) => {
  const modal = useModal();
  const t = useTranslations('personal');
  const authT = useTranslations('auth');

  const { selectedCountry } = useIpCountry();
  const { countries: countryList } = useCountries();
  const { mutateAsync: runBlackCheck } = useBlackCheck({
    mutationConfig: {}
  });
  const deviceId = useDeviceID() || '';
  const { checkCodeStatus, getCfToken } = useCfTokenCodeStatus();
  const { freshUserInfo } = useUserInfo();
  // 输入相关状态
  const [inputValue, setInputValue] = useState('');
  const [inputType, setInputType] = useState<InputType | null>(null);
  const [inputError, setInputError] = useState('');
  const [countryCode, setCountryCode] = useState(defaultPhoneCountry.code);

  // 获取手机号对应的国家信息
  const phoneCountry = countryList?.find(country => {
    if (country.mobileCode === countryCode) {
      return country;
    }
  });

  // 验证码相关状态
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [hasSentCode, setHasSentCode] = useState(false);

  // 输入框引用
  const inputRef = useRef<HTMLInputElement>(null);

  // 判断是否为手机号（3位或更多纯数字）
  const isPhoneNumber = (value: string): boolean => {
    const phoneRegex = /^\d{3,}$/;
    return phoneRegex.test(value);
  };

  // 判断输入类型
  const detectInputType = (value: string): InputType | null => {
    const trimmed = value.trim();
    if (!trimmed) return null;

    if (isEmail(trimmed)) {
      return InputType.EMAIL;
    } else if (isPhoneNumber(trimmed)) {
      return InputType.PHONE;
    }
    return null;
  };

  // 验证输入格式
  const validateInput = (value: string, type: InputType): { isValid: boolean; error?: string; } => {
    const trimmed = value.trim();

    if (!trimmed) {
      return { isValid: false, error: authT('errors.invalidAccount') };
    }

    if (type === InputType.EMAIL) {
      if (!isEmail(trimmed)) {
        return { isValid: false, error: t('form.validation.pleaseEnterValidEmailAddress') };
      }
    } else if (type === InputType.PHONE) {
      if (!/^[6-9]\d{9}$/.test(trimmed)) {
        return { isValid: false, error: t('form.validation.pleaseEnterValidPhoneNumber') };
      }
    }

    return { isValid: true };
  };

  // 是否可以提交
  const canSubmit = useMemo(() => {
    return inputValue.trim() && verificationCode.trim() && inputType;
  }, [inputValue, verificationCode, inputType]);

  // 处理输入变化
  const handleInputChange = (value: string) => {
    setInputValue(value);
    setInputError('');

    const detectedType = detectInputType(value);
    setInputType(detectedType);

    // 如果是手机号，更新国家代码
    if (detectedType === InputType.PHONE) {
      setCountryCode(selectedCountry?.mobileCode || defaultPhoneCountry.code);
    }
  };

  // 处理关闭
  const handleClose = () => {
    onClose?.();
    modal.resolve(undefined);
    modal.hide();
  };

  // 处理成功
  const handleSuccess = (token) => {
    toast(t('common.bindingSuccessful'));
    freshUserInfo();
    onSuccess?.();
    modal.resolve({ token });
    modal.hide();
  };
  const { authErrorCodeMap } = useAuthError();

  // 邮箱绑定 mutation
  const { mutate: emailBindMutation } = useEmailBind({
    mutationConfig: {
      onSuccess: (result) => {
        if (!result.success) {
          if (result.code === 102010) {
            setVerificationError(authT('errors.invalidCode'));
            return;
          }
          setInputError(authErrorCodeMap[result.code || 0] || result.msg || '');
          return;
        }
        handleSuccess(result.token);
      },
      onError: (error) => {
        console.error('Email bind error:', error);
      }
    }
  });

  // 手机绑定 mutation
  const { mutate: phoneBindMutation } = usePhoneBind({
    mutationConfig: {
      onSuccess: (result) => {
        if (!result.success) {
          if (result.code === 102010) {
            setVerificationError(authT('errors.invalidCode'));
            return;
          }
          setInputError(authErrorCodeMap[result.code || 0] || result.msg || '');
          return;
        }
        handleSuccess(result.token);
      },
      onError: (error) => {
        console.error('Phone bind error:', error);
      }
    }
  });

  // 邮箱发送验证码 mutation
  const { mutate: emailSendCodeMutation } = useEmailBindSendCode({
    mutationConfig: {
      onSuccess: (result) => {
        track('get_verification_code', {
          business_model: 'bind_email',
        });
        if (result.code) {
          const error = authErrorCodeMap[result.code || 0] || result.msg;
          setVerificationError(error || '');
          track('get_verification_code_result', {
            business_model: 'bind_email',
            is_success: false,
            fail_reason: result.msg,
          });
          return;
        }
        const codeRes = checkCodeStatus(result.codeStatus, 'bind_email');
        if (codeRes) {
          setHasSentCode(true);
          startCountdown(60, setResendCountdown);
          toast(t('common.verificationCodesent'));
          setInputError('');
          setVerificationError('');
        }
      },
      onError: (error) => {
        console.error('Email send code error:', error);
      }
    }
  });

  // 手机发送验证码 mutation
  const { mutate: phoneSendCodeMutation } = useBindSendCode({
    mutationConfig: {
      onSuccess: (result) => {
        track('get_verification_code', {
          business_model: 'bind_mobile',
        });
        if (result.code) {
          const error = authErrorCodeMap[result.code || 0] || result.msg;
          setVerificationError(error || '');
          track('get_verification_code_result', {
            business_model: 'bind_mobile',
            is_success: false,
            fail_reason: result.msg,
          });
          return;
        }
        const codeRes = checkCodeStatus(result.codeStatus, 'bind_mobile');
        if (codeRes) {
          setHasSentCode(true);
          startCountdown(60, setResendCountdown);
          toast(t('common.verificationCodesent'));
          setInputError('');
          setVerificationError('');
        }
      },
      onError: (error) => {
        console.error('Phone send code error:', error);
      }
    }
  });

  // 发送验证码
  const handleSendCode = async () => {
    if (!inputType) return;

    const validation = validateInput(inputValue, inputType);
    if (!validation.isValid) {
      setInputError(validation.error || '');
      return;
    }

    setInputError('');
    const [error, result] = await awaitTo(runBlackCheck({
      params: {
        account: inputValue.trim(),
        loginType: inputType === InputType.EMAIL ? VerifyMethod.Email : VerifyMethod.Phone,
        phoneCountryCode: inputType === InputType.PHONE ? `+${countryCode}` : undefined,
        deviceId
      }
    }));
    if (error) {
      setInputError(authT('errors.contactSupport'));
      return;
    }
    if (result.code) {
      const err = authErrorCodeMap[result.code || 0] || result.msg;
      setInputError(err || '');
      return;
    }
    if (result.blackStatus === BlackStatus.BLACK_STATUS_YES) {
      setInputError(authT('errors.contactSupport'));
      return;
    }
    const cfToken = await getCfToken();
    if (inputType === InputType.EMAIL) {
      emailSendCodeMutation({ params: { email: inputValue.trim(), cfToken } });
    } else if (inputType === InputType.PHONE) {
      phoneSendCodeMutation({ params: { phone: inputValue.trim(), phoneCountryCode: `+${countryCode}`, cfToken } });
    }
  };

  // 提交表单
  const handleSubmit = async () => {
    if (!inputType) return;

    const validation = validateInput(inputValue, inputType);
    if (!validation.isValid) {
      setInputError(validation.error || '');
      return;
    }

    // 验证验证码格式
    const codeValidation = inputType === InputType.EMAIL
      ? /^\d{4,6}$/.test(verificationCode.trim())
      : /^\d{6}$/.test(verificationCode.trim());

    if (!codeValidation) {
      setVerificationError(t('form.validation.pleaseEnter4To6DigitVerificationCode'));
      return;
    }

    setInputError('');
    setVerificationError('');

    if (inputType === InputType.EMAIL) {
      emailBindMutation({ params: { email: inputValue.trim(), code: verificationCode.trim() } });
    } else if (inputType === InputType.PHONE) {
      phoneBindMutation({ params: { phone: inputValue.trim(), code: verificationCode.trim(), phoneCountryCode: `+${countryCode}` } });
    }
  };

  // 渲染统一输入框
  const renderInputComponent = () => {
    return (
      <div className="relative">
        {/* 统一的输入框容器 */}
        <div className={'relative flex items-center text-white'}>
          {
            isPhoneNumber(inputValue) && (
              <div className="absolute left-[11px] flex items-center text-white text-[13px] font-medium">
                <Image
                  className='flex-shrink-0'
                  src={phoneCountry?.countryImageUrl || ''}
                  alt={phoneCountry?.en || ''}
                  width={15}
                  height={15}
                />
                <span className="px-[4px]">{phoneCountry?.code}</span>
                <span>+{countryCode}</span>
              </div>
            )
          }
          {/* 输入框 */}
          <input
            ref={inputRef}
            type="text"
            className={clsx('w-full focus:outline-none focus:comInputFocus comInput', {
              '!pl-[80px]': isPhoneNumber(inputValue),
              'errorOutline': inputError,
            })}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={inputType === InputType.EMAIL ? t('form.validation.pleaseEnterEmailAddress') : inputType === InputType.PHONE ? t('form.validation.pleaseEnterPhoneNumber') : t('form.validation.pleaseEnterEmailOrPhone')}
          />
          {
            inputValue.length > 0 && (
              <Image className="absolute right-[11px]" src={iconClose} alt="close" width={10} height={10} onClick={() => setInputValue('')} />
            )
          }
        </div>

        {inputError && (
          <div className="absolute bottom-0 translate-y-full left-0 errorText text-accent-red font-semibold text-[10px]">
            {inputError}
          </div>
        )}
      </div>
    );
  };

  return (
    <Drawer open={modal.visible} dismissible={false}>
      <DrawerTitle></DrawerTitle>
      <DrawerContent className={cn("bg-surface", className)}>
        <ModalTitle title={t('account.linkEmailOrMobile')} showBack={false} onClose={handleClose} />
        <div className='flex flex-col items-center px-[27px] my-5'>
          <p className="edit-form-tip mb-9">{t('account.linkSecurityDescription')}</p>

          <div className="flex-1 w-full px-[25px]">
            <div className={`${inputError ? 'mb-9' : 'mb-6'}`}>
              {renderInputComponent()}
            </div>

            <VerificationCodeInput
              code={verificationCode}
              onCodeChange={setVerificationCode}
              onResend={handleSendCode}
              countdown={resendCountdown}
              hasSentCode={hasSentCode}
              error={verificationError}
              className='h-[45px]'
            />
          </div>

          <div className="mt-9 flex items-center w-full">
            <Button className='flex-1 default-btn large-btn' onClick={handleClose}>
              {cancelText || t('common.cancel')}
            </Button>
            <Button disabled={!canSubmit} className='flex-1 primary-btn large-btn ml-2.5' onClick={handleSubmit}>
              {t('common.binding')}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
});

ModalEmailOrPhoneEdit.displayName = 'ModalEmailOrPhoneEdit';

export const showModalEmailOrPhoneEdit = (props: ModalEmailOrPhoneEditProps = {}): Promise<{ token: string; }> => {
  return NiceModal.show(ModalEmailOrPhoneEdit, props);
};

export default ModalEmailOrPhoneEdit;
